import { useState, useMemo, useCallback } from "react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/ui/dialog";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import {
  TriangleAlert,
  Search,
  ArrowLeft,
  Loader2,
  Briefcase,
  CreditCard,
  ChevronDown,
  X,
} from "lucide-react";
import {
  usePreviewJobSearch,
  useImportJobPostings,
} from "@/features/job-postings/queries/useJobPostings";
import { useActiveCompanies } from "@/features/admin/queries";
import type {
  JobSearchFilters,
  PreviewJobSearchResponse,
} from "@/services/api/job-postings";
import type { Company } from "@/services/api/companies";

interface ImportJobPostingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type DialogStep = "query" | "preview";

const EMPTY_FILTERS: JobSearchFilters = {
  companyUrl: undefined,
  companyName: undefined,
  title: undefined,
  description: undefined,
  location: undefined,
  country: undefined,
  seniority: undefined,
  employmentType: undefined,
  activeOnly: true,
  postedAfter: undefined,
};

export function ImportJobPostingsDialog({
  open,
  onOpenChange,
}: ImportJobPostingsDialogProps) {
  const [step, setStep] = useState<DialogStep>("query");
  const [filters, setFilters] = useState<JobSearchFilters>(EMPTY_FILTERS);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [companyMode, setCompanyMode] = useState<"linkedInUrl" | "companyName">(
    "linkedInUrl",
  );
  const [previewResult, setPreviewResult] =
    useState<PreviewJobSearchResponse | null>(null);

  const { data: companies = [] } = useActiveCompanies({ enabled: open });
  const previewJobSearch = usePreviewJobSearch();
  const importJobPostings = useImportJobPostings();

  const hasFilters = useMemo(() => {
    return (
      !!filters.companyUrl ||
      !!filters.companyName ||
      (filters.title?.length ?? 0) > 0 ||
      (filters.description?.length ?? 0) > 0 ||
      !!filters.location ||
      !!filters.country ||
      !!filters.seniority ||
      !!filters.employmentType
    );
  }, [filters]);

  const canPreview = hasFilters || !!selectedCompanyId;

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompanyId) ?? null,
    [companies, selectedCompanyId],
  );

  const updateFilter = useCallback(
    <K extends keyof JobSearchFilters>(
      key: K,
      value: JobSearchFilters[K],
    ) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
    if (companyId) {
      const company = companies.find((c) => c.id === companyId);
      if (company) {
        setFilters((prev) => ({
          ...prev,
          companyUrl: company.linkedInUrl,
          companyName: undefined,
        }));
      }
    } else {
      setFilters((prev) => ({
        ...prev,
        companyUrl: undefined,
        companyName: undefined,
      }));
    }
  };

  const handlePreview = async () => {
    try {
      const result = await previewJobSearch.mutateAsync({
        filters,
        itemsPerPage: 20,
      });
      setPreviewResult(result);
      setStep("preview");
    } catch {
      // Error handled by mutation onError
    }
  };

  const handleImport = async () => {
    try {
      await importJobPostings.mutateAsync({ filters });
      resetAndClose();
    } catch {
      // Error handled by mutation onError
    }
  };

  const resetAndClose = () => {
    setFilters(EMPTY_FILTERS);
    setSelectedCompanyId(null);
    setCompanyMode("linkedInUrl");
    setPreviewResult(null);
    setStep("query");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto">
        {step === "query" && (
          <QueryStep
            companies={companies}
            selectedCompanyId={selectedCompanyId}
            onCompanySelect={handleCompanySelect}
            companyMode={companyMode}
            onCompanyModeChange={setCompanyMode}
            filters={filters}
            updateFilter={updateFilter}
            canPreview={canPreview}
            hasFilters={hasFilters}
            isLoading={previewJobSearch.isPending}
            onPreview={handlePreview}
            onCancel={resetAndClose}
          />
        )}

        {step === "preview" && previewResult && (
          <PreviewStep
            previewResult={previewResult}
            filters={filters}
            companyName={selectedCompany?.name}
            isImporting={importJobPostings.isPending}
            onBack={() => setStep("query")}
            onImport={handleImport}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function QueryStep({
  companies,
  selectedCompanyId,
  onCompanySelect,
  companyMode,
  onCompanyModeChange,
  filters,
  updateFilter,
  canPreview,
  hasFilters,
  isLoading,
  onPreview,
  onCancel,
}: {
  companies: Company[];
  selectedCompanyId: number | null;
  onCompanySelect: (id: number | null) => void;
  companyMode: "linkedInUrl" | "companyName";
  onCompanyModeChange: (mode: "linkedInUrl" | "companyName") => void;
  filters: JobSearchFilters;
  updateFilter: <K extends keyof JobSearchFilters>(
    key: K,
    value: JobSearchFilters[K],
  ) => void;
  canPreview: boolean;
  hasFilters: boolean;
  isLoading: boolean;
  onPreview: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Import Job Postings</DialogTitle>
        <DialogDescription>
          Search LinkedIn job postings via CoreSignal and import them into the
          database.
        </DialogDescription>
      </DialogHeader>

      <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
        <TriangleAlert className="h-4 w-4 shrink-0 text-amber-500 mt-px" />
        <p>
          Searching and importing job postings consumes CoreSignal API credits.
          Preview costs 1 search credit; importing costs 1 collect credit per
          new posting.
        </p>
      </div>

      <div className="space-y-4 py-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-ink-600">
            Company
          </label>
          <div className="flex items-center gap-3 mb-2">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="jp-company-mode"
                checked={companyMode === "linkedInUrl"}
                onChange={() => {
                  onCompanyModeChange("linkedInUrl");
                  updateFilter("companyName", undefined);
                }}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-ink-700">LinkedIn URL</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="jp-company-mode"
                checked={companyMode === "companyName"}
                onChange={() => {
                  onCompanyModeChange("companyName");
                  onCompanySelect(null);
                  updateFilter("companyUrl", undefined);
                }}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-ink-700">Company Name</span>
            </label>
          </div>

          {companyMode === "linkedInUrl" ? (
            <div>
              <div className="relative">
                <select
                  className="w-full appearance-none rounded-md border border-grid-200 bg-white px-3 py-2 pr-8 text-sm text-ink-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={selectedCompanyId ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    onCompanySelect(val ? Number(val) : null);
                  }}
                >
                  <option value="">Select a company...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              </div>
              {selectedCompanyId && filters.companyUrl && (
                <p className="mt-1 text-xs text-ink-400 truncate">
                  {filters.companyUrl}
                </p>
              )}
            </div>
          ) : (
            <Input
              type="text"
              placeholder='e.g. "General Motors", "BMW"'
              value={filters.companyName || ""}
              onChange={(e) =>
                updateFilter("companyName", e.target.value || undefined)
              }
            />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-ink-700">Search Filters</h3>
            <span className="text-xs text-ink-400">(optional)</span>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Title Keywords
              </label>
              <KeywordTagInput
                values={filters.title ?? []}
                onChange={(v) =>
                  updateFilter("title", v.length > 0 ? v : undefined)
                }
                placeholder="Type a phrase and press Enter"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Description Keywords
              </label>
              <KeywordTagInput
                values={filters.description ?? []}
                onChange={(v) =>
                  updateFilter("description", v.length > 0 ? v : undefined)
                }
                placeholder="Type a phrase and press Enter"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Location
              </label>
              <Input
                type="text"
                placeholder='e.g. "Milton Keynes"'
                value={filters.location || ""}
                onChange={(e) =>
                  updateFilter("location", e.target.value || undefined)
                }
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Country
              </label>
              <Input
                type="text"
                placeholder='e.g. "United Kingdom"'
                value={filters.country || ""}
                onChange={(e) =>
                  updateFilter("country", e.target.value || undefined)
                }
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Seniority
              </label>
              <Input
                type="text"
                placeholder='e.g. "Director", "Senior"'
                value={filters.seniority || ""}
                onChange={(e) =>
                  updateFilter("seniority", e.target.value || undefined)
                }
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Employment Type
              </label>
              <Input
                type="text"
                placeholder='e.g. "Full-time", "Contract"'
                value={filters.employmentType || ""}
                onChange={(e) =>
                  updateFilter("employmentType", e.target.value || undefined)
                }
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">
              Posted After
            </label>
            <Input
              type="month"
              placeholder="YYYY-MM"
              value={filters.postedAfter || ""}
              onChange={(e) =>
                updateFilter("postedAfter", e.target.value || undefined)
              }
              className="max-w-[200px]"
            />
            <p className="mt-1 text-xs text-ink-400">
              Only return postings created after this month
            </p>
          </div>

          <div className="flex items-center space-x-2 pt-1">
            <Checkbox
              id="active-postings-only"
              checked={filters.activeOnly ?? true}
              onCheckedChange={(checked) =>
                updateFilter("activeOnly", checked === true)
              }
            />
            <label
              htmlFor="active-postings-only"
              className="text-sm text-ink-700 cursor-pointer"
            >
              Active postings only
            </label>
          </div>
        </div>

        {(canPreview || hasFilters) && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
            <QuerySummary
              companyName={
                companies.find((c) => c.id === selectedCompanyId)?.name
              }
              filters={filters}
            />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onPreview} disabled={!canPreview || isLoading}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Preview Results (1 search credit)
        </Button>
      </DialogFooter>
    </>
  );
}

function PreviewStep({
  previewResult,
  filters,
  companyName,
  isImporting,
  onBack,
  onImport,
}: {
  previewResult: PreviewJobSearchResponse;
  filters: JobSearchFilters;
  companyName?: string;
  isImporting: boolean;
  onBack: () => void;
  onImport: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Preview & Import</DialogTitle>
        <DialogDescription>
          Review the results, then import or go back to refine.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="rounded-lg border border-grid-200 bg-grid-50 px-3 py-2.5">
          <QuerySummary companyName={companyName} filters={filters} />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Briefcase className="h-4 w-4 text-ink-500" />
            <span className="font-medium text-ink-900">
              {previewResult.totalCount.toLocaleString()}
            </span>
            <span className="text-ink-500">job postings found</span>
          </div>
          {previewResult.remainingCredits != null && (
            <div className="flex items-center gap-2 text-sm text-ink-500">
              <CreditCard className="h-4 w-4" />
              <span>
                {previewResult.remainingCredits.toLocaleString()} search credits
                remaining
              </span>
            </div>
          )}
        </div>

        {previewResult.jobPostings.length > 0 ? (
          <div className="border border-grid-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-grid-200 bg-grid-50">
                    <th className="px-3 py-2 text-left font-medium text-ink-600">
                      Title
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-ink-600">
                      Company
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-ink-600">
                      Location
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-ink-600">
                      Posted
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewResult.jobPostings.map((posting) => (
                    <tr
                      key={posting.id}
                      className="border-b border-grid-100 last:border-0"
                    >
                      <td className="px-3 py-2 text-ink-900 font-medium">
                        {posting.title || "—"}
                      </td>
                      <td className="px-3 py-2 text-ink-700">
                        {posting.companyName || "—"}
                      </td>
                      <td className="px-3 py-2 text-ink-500 text-xs">
                        {posting.location || "—"}
                      </td>
                      <td className="px-3 py-2 text-ink-500 text-xs">
                        {posting.createdAt
                          ? new Date(posting.createdAt).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewResult.totalCount > previewResult.jobPostings.length && (
              <div className="px-3 py-2 text-xs text-ink-400 bg-grid-50 border-t border-grid-200 text-center">
                Showing {previewResult.jobPostings.length} of{" "}
                {previewResult.totalCount.toLocaleString()} job postings
              </div>
            )}
          </div>
        ) : (
          <div className="border border-grid-200 rounded-lg p-8 text-center text-sm text-ink-500">
            No job postings matched your query. Go back to refine your filters.
          </div>
        )}

        {previewResult.totalCount > 0 && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <TriangleAlert className="h-4 w-4 shrink-0 text-amber-500 mt-px" />
            <p>
              This import will consume up to{" "}
              <strong>
                {previewResult.totalCount} collect credits
              </strong>{" "}
              for new postings. Already imported postings will be skipped
              automatically.
            </p>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack} disabled={isImporting}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Refine Query
        </Button>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={previewResult.totalCount === 0 || isImporting}
        >
          Import {previewResult.totalCount.toLocaleString()} Job Postings
        </Button>
      </DialogFooter>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Confirm Import</DialogTitle>
            <DialogDescription>
              Are you sure you want to import{" "}
              <strong>
                {previewResult.totalCount.toLocaleString()} job postings
              </strong>
              ? This will consume up to{" "}
              <strong>
                {previewResult.totalCount.toLocaleString()} collect credits
              </strong>
              .
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                onImport();
              }}
              disabled={isImporting}
            >
              {isImporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Confirm Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function KeywordTagInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  const addKeyword = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (values.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  const removeKeyword = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-md border border-grid-200 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2.5 pt-2">
          {values.map((keyword, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs font-medium text-blue-800"
            >
              {keyword}
              {i < values.length - 1 && (
                <span className="text-blue-400 font-normal -mr-0.5 ml-0.5 text-[10px]">
                  OR
                </span>
              )}
              <button
                type="button"
                onClick={() => removeKeyword(i)}
                className="ml-0.5 rounded hover:bg-blue-100 p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="w-full border-0 bg-transparent px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
        placeholder={values.length > 0 ? "Add another keyword..." : placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addKeyword();
          }
          if (e.key === "Backspace" && !draft && values.length > 0) {
            removeKeyword(values.length - 1);
          }
        }}
        onBlur={addKeyword}
      />
    </div>
  );
}

function buildQuerySummary(
  companyName: string | undefined,
  filters: JobSearchFilters,
): string {
  const parts: string[] = [];

  if (companyName) {
    parts.push(`Jobs from **${companyName}**`);
  } else if (filters.companyName) {
    parts.push(`Jobs from **${filters.companyName}**`);
  } else if (filters.companyUrl) {
    parts.push(`Jobs at **${filters.companyUrl}**`);
  } else {
    parts.push("Job postings");
  }

  if (filters.title?.length) {
    const joined = filters.title.map((k) => `**"${k}"**`).join(" or ");
    parts.push(`with ${joined} in title`);
  }

  if (filters.description?.length) {
    const joined = filters.description.map((k) => `**"${k}"**`).join(" or ");
    parts.push(`with ${joined} in description`);
  }

  if (filters.seniority) {
    parts.push(`at **${filters.seniority}** level`);
  }

  if (filters.employmentType) {
    parts.push(`(**${filters.employmentType}**)`);
  }

  if (filters.location) {
    parts.push(`in **${filters.location}**`);
  } else if (filters.country) {
    parts.push(`in **${filters.country}**`);
  }

  if (filters.activeOnly) {
    parts.push("(active only)");
  }

  if (filters.postedAfter) {
    parts.push(`since **${filters.postedAfter}**`);
  }

  return parts.join(", ");
}

function QuerySummary({
  companyName,
  filters,
}: {
  companyName?: string;
  filters: JobSearchFilters;
}) {
  const raw = buildQuerySummary(companyName, filters);

  const rendered = raw.split(/(\*\*[^*]+\*\*)/).map((segment, i) => {
    if (segment.startsWith("**") && segment.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-ink-900">
          {segment.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{segment}</span>;
  });

  return <p className="text-sm text-ink-600 leading-relaxed">{rendered}</p>;
}
